import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Plus , UserCircle} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { DentistFilters, DentistFiltersData } from "@/components/dentists/DentistFilters";
import { DentistsTable } from "@/components/dentists/DentistsTable";
import { CreateEditDentistDialog } from "@/components/dentists/CreateEditDentistDialog";
import { DeleteDentistDialog } from "@/components/dentists/DeleteDentistDialog";
import {
  Dentist,
  CreateDentistData,
  fetchDentists,
  createDentist,
  updateDentist,
  activateDentist,
  inactivateDentist,
  deleteDentist,
} from "@/services/dentist-service";

export default function Dentists() {
  const [dentists, setDentists] = useState<Dentist[]>([]);
  const [loading, setLoading] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [filters, setFilters] = useState<DentistFiltersData>({});
  const [selectedDentist, setSelectedDentist] = useState<Dentist | null>(null);
  const [isCreateEditDialogOpen, setIsCreateEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { toast } = useToast();

  const loadDentists = async () => {
    setLoading(true);
    try {
      const data = await fetchDentists(
        includeInactive,
        false,
        filters.nameLike,
        1,
        filters.pageSize || 20
      );
      setDentists(data);
    } catch (error) {
      toast({
        title: "Erro ao carregar profissionais",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDentists();
  }, [filters, includeInactive]);

  const handleFilterChange = (newFilters: DentistFiltersData) => {
    setFilters(newFilters);
  };

  const handleCreateNew = () => {
    setSelectedDentist(null);
    setIsCreateEditDialogOpen(true);
  };

  const handleEdit = (dentist: Dentist) => {
    setSelectedDentist(dentist);
    setIsCreateEditDialogOpen(true);
  };

  const handleDelete = (dentist: Dentist) => {
    setSelectedDentist(dentist);
    setIsDeleteDialogOpen(true);
  };

  const handleSave = async (data: CreateDentistData, id?: string) => {
    try {
      if (id) {
        await updateDentist(id, data);
        toast({
          title: "Profissional atualizado!",
          description: "As alterações foram salvas com sucesso.",
        });
      } else {
        await createDentist(data);
        toast({
          title: "Profissional criado!",
          description: "O profissional foi cadastrado com sucesso.",
        });
      }
      loadDentists();
    } catch (error) {
      toast({
        title: id ? "Erro ao atualizar profissional" : "Erro ao criar profissional",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
      throw error;
    }
  };

  const handleConfirmDelete = async (dentistId: string) => {
    try {
      await deleteDentist(dentistId);
      toast({
        title: "Profissional excluído",
        description: "O profissional foi removido com sucesso.",
      });
      loadDentists();
    } catch (error) {
      toast({
        title: "Erro ao excluir profissional",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    }
  };

  const handleActivate = async (dentist: Dentist) => {
    try {
      await activateDentist(dentist.id);
      toast({
        title: "Profissional ativado",
        description: `${dentist.name} foi ativado com sucesso.`,
      });
      loadDentists();
    } catch (error) {
      toast({
        title: "Erro ao ativar profissional",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    }
  };

  const handleInactivate = async (dentist: Dentist) => {
    try {
      await inactivateDentist(dentist.id);
      toast({
        title: "Profissional inativado",
        description: `${dentist.name} foi inativado com sucesso.`,
      });
      loadDentists();
    } catch (error) {
      toast({
        title: "Erro ao inativar profissional",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="page-wrapper">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
              <UserCircle className="page-icon" />
              <h1 className="page-title">Profissionais</h1>
            </div>
          <p className="page-subtitle">
            Gerencie os profissionais odontológicos
          </p>
        </div>
        <Button size="sm" onClick={handleCreateNew} className="btn-header">
          <Plus className="btn-header-icon" />
          Novo Profissional
        </Button>
      </div>

      <DentistFilters
        onFilterChange={handleFilterChange}
        includeInactive={includeInactive}
        onIncludeInactiveChange={setIncludeInactive}
      />

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="page-counter">
              {dentists.length} profissional(is) encontrado(s)
            </p>
          </div>

        <DentistsTable
          dentists={dentists}
          loading={loading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onActivate={handleActivate}
          onInactivate={handleInactivate}
        />

        <CreateEditDentistDialog
          open={isCreateEditDialogOpen}
          onOpenChange={setIsCreateEditDialogOpen}
          dentist={selectedDentist}
          onSave={handleSave}
        />

        <DeleteDentistDialog
          dentist={selectedDentist}
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
          onDelete={handleConfirmDelete}
        />
      </div>
    </div>
  );
}